// wordingRules.ts
import { parseChainValue } from './measurementChain';
import { diastolicGrade } from './echoCalc';
import { AORTA_ASCENDING_SLOT, AORTA_SLOT } from './aortaSlot';

/**
 * Smart wording — choosing a report sentence's phrase from the measurements.
 *
 * Priority #2 of the Smart Reporting work (Nikhil, 25 Aug call): "if a value is high, the
 * report text automatically reflects that finding". A rule reads the measurements already
 * on the report and says which GRADE the sentence should express — never the literal
 * words. Turning a grade into words is `wordingVocabulary.ts`, because the phrasing
 * belongs to the clinic, not to us.
 *
 * WHERE THE NUMBERS COME FROM
 * Lang et al., "Recommendations for Cardiac Chamber Quantification by Echocardiography in
 * Adults", JASE 2015;28:1-53 (ASE/EACVI) — sent by Dr Shashi, 31 Aug. Its Supplemental
 * Table 3 carries four-tier, sex-specific partitions for LV dimensions, volumes and wall
 * thickness, which is what unblocked the two LV chamber rules below.
 *
 * Read that table's standing warning before adding to it. The committee endorsed partition
 * values for ONLY LV EF and LA volume; everything else was an "experience-based" compromise
 * exiled to the Appendix, over the objection of members who thought publishing them was a
 * disservice. The paper's own words: "All partition values should be interpreted with
 * caution." So the two rules that shipped first are the two that were never in doubt, and
 * the ones below them stand on weaker ground by the authors' own account.
 *
 * WHAT IS DELIBERATELY ABSENT
 * One of the nine proposed rules is not here: RV systolic function. Its thresholds exist
 * (TAPSE < 1.7 cm, S′ < 9.5 cm/s) but the paper declines to name a primary parameter or say
 * what to do when the two disagree — and, decisively, the clinic's own list cannot express
 * the result. Its normal option reads "and systolic function", which carries no word the
 * matcher recognises, and a binary threshold gives no mild/moderate/severe to write
 * instead. The rule could never produce a phrase, so it is not written.
 *
 * RA size and RV size ARE here but write only the normal side, because the paper gives one
 * cut-off each rather than bands — see those rules for the reasoning.
 *
 * Beware Table 10 when adding RV rules: its E/A, e′, E/e′ rows are TRICUSPID inflow
 * values, and its "E/e′ > 6.0" is an RV threshold. The LV one is > 14, as used below.
 *
 * Sex-specific reference ranges are IN USE (confirmed 26 Aug). Where sex is unknown the
 * rule falls back to the male range, which is the wider one — erring toward calling a
 * borderline value normal rather than flagging a healthy patient.
 */

export type Sex = 'M' | 'F' | null;

/** What a sentence should express. Deliberately not words — see the module comment. */
export type WordingIntent =
  | 'normal'
  | 'mild'
  | 'moderate'
  | 'severe'
  | 'elevated';

export type WordingSuggestion = {
  /** The `data-editable` span's class, i.e. which sentence this is about. */
  slot: string;
  intent: WordingIntent;
  /**
   * Advisory only — report the finding, never write it into the document.
   *
   * Dr Shashi, 07 Sep: *"Diastolic dysfunction is not straight forward math. It's
   * complicated and tech or doctors would need to change the findings as many things
   * determine it."* He is right, and it is not a limitation of this implementation: the
   * ASE algorithm is a decision tree over markers that are themselves invalid in atrial
   * fibrillation, significant mitral disease, a paced rhythm and several other states
   * this engine cannot detect from an SR. A grade written automatically into a signed
   * report asserts a judgement the data cannot carry.
   *
   * So these rules still RUN — the computed grade is genuinely useful to a reporting
   * clinician — but it reaches them as a note beside the report, never as words in it.
   */
  advisory?: boolean;
  /** Shown to the clinician when the rule disagrees with what the report says. */
  reason: string;
  /** True when the driving value sits within 5% of a threshold. */
  borderline: boolean;
};

const num = (v: unknown): number | null => parseChainValue(v);

const normalizeSex = (value: unknown): Sex => {
  const text = String(value ?? '').trim().toLowerCase();
  if (text.startsWith('m')) {
    return 'M';
  }
  if (text.startsWith('f')) {
    return 'F';
  }
  return null;
};

/** Within 5% of the nearest band edge — worth telling the clinician about. */
const isBorderline = (value: number, edges: number[]): boolean =>
  edges.some(edge => edge !== 0 && Math.abs(value - edge) / Math.abs(edge) <= 0.05);

/**
 * LV systolic function from ejection fraction.
 *
 * ASE 2015: normal ≥52% (male) / ≥54% (female); 41–51 mildly reduced, 30–40 moderately
 * reduced, <30 severely reduced. The normal cut-offs are the ones already in
 * `measurementCatalog.ts`, so the printed abnormal flag and the sentence cannot disagree.
 *
 * The clinic list also offers "preserved". The rule never writes it: preserved and normal
 * are not interchangeable in every context, so that choice stays with the clinician.
 */
function lvSystolicFunction(values: Record<string, unknown>, sex: Sex): WordingSuggestion | null {
  const ef = num(values.EF_Biplane);
  if (ef === null) {
    return null;
  }
  const normalFloor = sex === 'F' ? 54 : 52;
  const edges = [normalFloor, 41, 30];
  const borderline = isBorderline(ef, edges);

  let intent: WordingIntent;
  if (ef >= normalFloor) {
    intent = 'normal';
  } else if (ef >= 41) {
    intent = 'mild';
  } else if (ef >= 30) {
    intent = 'moderate';
  } else {
    intent = 'severe';
  }

  return {
    slot: 'lv-normal-second',
    intent,
    reason: `EF ${ef}%${sex ? ` (${sex === 'F' ? 'female' : 'male'} reference)` : ''}`,
    borderline,
  };
}

/**
 * LA size from left atrial volume index.
 *
 * ASE 2015 partition values, the same for both sexes: ≤34 normal, 35–41 mild, 42–48
 * moderate, >48 severe. These are published bands rather than convention, which is why
 * this rule ships ahead of the LV chamber ones.
 */
function laSize(values: Record<string, unknown>): WordingSuggestion | null {
  const lavi = num(values.LA_Vol_Ind);
  if (lavi === null) {
    return null;
  }
  const borderline = isBorderline(lavi, [34, 41, 48]);

  let intent: WordingIntent;
  if (lavi <= 34) {
    intent = 'normal';
  } else if (lavi <= 41) {
    intent = 'mild';
  } else if (lavi <= 48) {
    intent = 'moderate';
  } else {
    intent = 'severe';
  }

  return {
    slot: 'la-normal',
    intent,
    reason: `LA volume index ${lavi} ml/m²`,
    borderline,
  };
}

/**
 * LV size, from the indexed end-diastolic volume where it exists and the internal diameter
 * otherwise.
 *
 * ASE 2015 Supplemental Table 3, sex-specific, in the units the catalog already stores
 * (LVIDd in cm, LVEDVI in mL/m²):
 *
 *              normal      mild        moderate    severe
 *   LVIDd  M   4.2-5.8     5.9-6.3     6.4-6.8     >6.8
 *          F   3.8-5.2     5.3-5.6     5.7-6.1     >6.1
 *   LVEDVI M   34-74       75-89       90-100      >100
 *          F   29-61       62-70       71-80       >80
 *
 * Volume is preferred because the paper says so: a ventricle with a normal base but an
 * enlarged mid-cavity and apex reads as normal on a basal diameter, and "LV volume would be
 * a better marker of LV size than linear dimension measured at the LV base". The diameter
 * is the fallback, not the equal.
 *
 * BELOW the normal floor the rule says nothing. The clinic list has a separate "Small"
 * option, so a 3.9 cm male LVIDd is neither normal nor dilated, and the paper grades only
 * one direction. Writing "Normal" there would be wrong in the one way the clinician would
 * not think to check.
 */
function lvSize(values: Record<string, unknown>, sex: Sex): WordingSuggestion | null {
  const female = sex === 'F';

  const lvedvi = num(values.LVEDVI);
  const lvidd = num(values.LVIDd);

  // [floor, mild ceiling, moderate ceiling] — above the last is severe.
  let value: number;
  let bands: [number, number, number];
  let reason: string;

  if (lvedvi !== null) {
    value = lvedvi;
    bands = female ? [29, 70, 80] : [34, 89, 100];
    reason = `LV EDV index ${lvedvi} ml/m²`;
  } else if (lvidd !== null) {
    value = lvidd;
    bands = female ? [3.8, 5.6, 6.1] : [4.2, 6.3, 6.8];
    reason = `LVIDd ${lvidd} cm`;
  } else {
    return null;
  }

  const [floor, mildCeiling, moderateCeiling] = bands;
  const normalCeiling = lvedvi !== null ? (female ? 61 : 74) : female ? 5.2 : 5.8;

  // Undersized: the paper grades dilatation only, and the clinic word is "Small".
  if (value < floor) {
    return null;
  }

  let intent: WordingIntent;
  if (value <= normalCeiling) {
    intent = 'normal';
  } else if (value <= mildCeiling) {
    intent = 'mild';
  } else if (value <= moderateCeiling) {
    intent = 'moderate';
  } else {
    intent = 'severe';
  }

  return {
    slot: 'lv-normal-one',
    intent,
    reason: `${reason}${sex ? ` (${female ? 'female' : 'male'} reference)` : ''}`,
    borderline: isBorderline(value, [normalCeiling, mildCeiling, moderateCeiling]),
  };
}

/**
 * LV wall thickness, from the thicker of the septum and the posterior wall.
 *
 * ASE 2015 Supplemental Table 3, in cm:
 *
 *              normal      mild        moderate    severe
 *   IVSd/PWd M 0.6-1.0     1.1-1.3     1.4-1.6     >1.6
 *          F 0.6-0.9     1.0-1.2     1.3-1.5     >1.5
 *
 * The thicker wall drives the grade. Asymmetric hypertrophy is still hypertrophy, and
 * averaging the two would let a 1.7 cm septum hide behind a normal posterior wall.
 *
 * GATED ON RELATIVE WALL THICKNESS. The clinic's own phrase for the mild grade reads
 * "Mild concentric increase in LV", and concentric is not a wall-thickness finding — it is
 * RWT > 0.42 (paper §4, Figure 6). A thick wall with RWT <= 0.42 is ECCENTRIC hypertrophy,
 * and printing the clinic's concentric phrase there would state the wrong geometry in a
 * signed report. So an abnormal grade is emitted only when RWT confirms concentric
 * remodelling; when RWT is missing or eccentric, the rule stays silent and the clinician
 * writes the sentence.
 *
 * Normal is not gated — RWT has no bearing on walls that are not thick.
 *
 * NOT DECIDED HERE: whether LV mass index should override wall thickness when the two
 * disagree. The paper offers no precedence rule, only the four geometry classes, so a
 * normal-mass / thick-wall study still grades off the wall. Dr Shashi's call, and the one
 * open question left in this rule.
 */
function lvWallThickness(values: Record<string, unknown>, sex: Sex): WordingSuggestion | null {
  const ivsd = num(values.IVSd);
  const lvpwd = num(values.LVPWd);

  const walls = [ivsd, lvpwd].filter((w): w is number => w !== null);
  if (walls.length === 0) {
    return null;
  }
  const thickest = Math.max(...walls);

  const female = sex === 'F';
  const normalCeiling = female ? 0.9 : 1.0;
  const mildCeiling = female ? 1.2 : 1.3;
  const moderateCeiling = female ? 1.5 : 1.6;

  const label =
    ivsd !== null && lvpwd !== null
      ? `IVSd ${ivsd} cm, LVPWd ${lvpwd} cm`
      : ivsd !== null
        ? `IVSd ${ivsd} cm`
        : `LVPWd ${lvpwd} cm`;
  const sexNote = sex ? ` (${female ? 'female' : 'male'} reference)` : '';
  const borderline = isBorderline(thickest, [normalCeiling, mildCeiling, moderateCeiling]);

  if (thickest <= normalCeiling) {
    return {
      slot: 'lv-normal-third',
      intent: 'normal',
      reason: `${label}${sexNote}`,
      borderline,
    };
  }

  // Thickened — but the clinic's wording asserts concentric geometry, so RWT must agree.
  const rwt = num(values.RWT);
  if (rwt === null || rwt <= 0.42) {
    return null;
  }

  let intent: WordingIntent;
  if (thickest <= mildCeiling) {
    intent = 'mild';
  } else if (thickest <= moderateCeiling) {
    intent = 'moderate';
  } else {
    intent = 'severe';
  }

  return {
    slot: 'lv-normal-third',
    intent,
    reason: `${label}${sexNote}, RWT ${rwt} (concentric)`,
    borderline,
  };
}

/**
 * Filling pressures — normal or elevated.
 *
 * The three ASE left-atrial-pressure markers: E/e′ average > 14, LA volume index > 34,
 * TR Vmax > 2.8 m/s. Two or more abnormal reads as elevated.
 *
 * Requires at least two of the three to have been measured. With one marker there is no
 * majority to take, and guessing from a single value is exactly the kind of call this
 * engine must not make.
 *
 * One raised out of only TWO available is INDETERMINATE, not normal, and the rule stays
 * silent. With all three markers, one raised is genuinely normal — but with two, a single
 * raised marker is a split decision, and writing "Normal filling pressures" there would
 * assert something the data does not support. Found while testing a study with LAVI 45
 * raised, E/e' normal and no TR jet: the rule called it Normal, and would have overwritten
 * a clinician who had put "Elevated".
 */
function fillingPressures(values: Record<string, unknown>): WordingSuggestion | null {
  const ee = num(values.Avg_EE);
  const lavi = num(values.LA_Vol_Ind);
  const tr = num(values.TR_Vmax);

  const markers: Array<{ abnormal: boolean; label: string }> = [];
  if (ee !== null) {
    markers.push({ abnormal: ee > 14, label: `E/e′ ${ee}` });
  }
  if (lavi !== null) {
    markers.push({ abnormal: lavi > 34, label: `LAVI ${lavi}` });
  }
  if (tr !== null) {
    markers.push({ abnormal: tr > 2.8, label: `TR Vmax ${tr}` });
  }

  if (markers.length < 2) {
    return null;
  }

  const abnormal = markers.filter(m => m.abnormal);

  // A split decision on two markers is indeterminate — say nothing.
  if (abnormal.length === 1 && markers.length === 2) {
    return null;
  }

  const borderline =
    (ee !== null && isBorderline(ee, [14])) ||
    (lavi !== null && isBorderline(lavi, [34])) ||
    (tr !== null && isBorderline(tr, [2.8]));

  return {
    slot: 'lv-normal-fifth',
    intent: abnormal.length >= 2 ? 'elevated' : 'normal',
    reason: `${abnormal.length} of ${markers.length} markers raised (${markers
      .map(m => m.label)
      .join(', ')})`,
    borderline,
  };
}

/**
 * RV size, and RA size — the normal side only.
 *
 * ASE 2015 gives ONE threshold for each, not four bands: "a diameter > 41 mm at the base
 * and > 35 mm at the midlevel in the RV-focused view indicates RV dilatation", and RA
 * volume as a mean ± SD with no partitions at all. Both clinic lists offer mild, moderate
 * and severe, and there is no published basis for choosing between them.
 *
 * So these rules write "Normal" when the measurement is within range and NOTHING when it
 * is not. Half a rule by appearance, but the useful half: most studies are normal, so the
 * sentence fills itself on the majority of reports and stays quiet on exactly the ones
 * that need a clinician to grade them.
 *
 * Deliberately no `mild` fallback for "some abnormality". Writing "Mildly dilated" for
 * every dilated right ventricle would understate a severe one, in the sentence a reader
 * scans for exactly that.
 */
function rvSize(values: Record<string, unknown>): WordingSuggestion | null {
  const basal = num(values.RV_basal);
  if (basal === null || basal > 4.1) {
    return null;
  }
  return {
    slot: 'rv-normal',
    intent: 'normal',
    reason: `RV basal diameter ${basal} cm`,
    borderline: isBorderline(basal, [4.1]),
  };
}

function raSize(values: Record<string, unknown>): WordingSuggestion | null {
  const area = num(values.RA_area);
  if (area === null || area > 18) {
    return null;
  }
  return {
    slot: 'ra-normal',
    intent: 'normal',
    reason: `RA area ${area} cm²`,
    borderline: isBorderline(area, [18]),
  };
}

/**
 * Diastolic function — Normal / Grade I / Grade II / Grade III.
 *
 * The grading itself is `echoCalc.diastolicGrade`, which implements the ASE 2025 algorithm
 * — a decade newer than the 2015 chamber-quantification paper, which does not cover
 * diastology at all. This rule is the wiring, not the medicine: it collects the inputs,
 * calls the engine, and maps its code onto a grade the clinic list can express. The
 * option names ("Grade I/II/III") already line up with the `mild`/`moderate`/`severe`
 * synonyms, so no new vocabulary is needed.
 *
 * TWO GUARDS, both of which mean silence rather than a guess:
 *
 * 1. Code 9 is Indeterminate — the engine's own signal that the markers are too sparse or
 *    too split to decide. That is a real clinical category, but the clinic list cannot say
 *    it (Normal / Grade I / II / III), and mapping it onto any of those would be a
 *    fabrication. Same reasoning as the filling-pressure split.
 *
 * 2. Atrial fibrillation. The algorithm assumes sinus rhythm — E/A has no meaning without
 *    an A wave, and the engine's own header lists AF among the special populations it does
 *    not handle. The report already knows: `ReportsLayout` tracks the study's AF mark and
 *    passes it in. Mitral stenosis and severe MR are the other exclusions, but neither is
 *    a flag we currently carry, so they stay a clinician's judgement.
 */
function diastolicFunction(
  values: Record<string, unknown>,
  atrialFibrillation: boolean
): WordingSuggestion | null {
  if (atrialFibrillation) {
    return null;
  }

  const grade = diastolicGrade({
    eSeptal: num(values.e_prime_septal),
    eLateral: num(values.e_prime_lateral),
    eOverEprime: num(values.Avg_EE),
    trVmax: num(values.TR_Vmax),
    pasp: num(values.RVSP),
    lavi: num(values.LA_Vol_Ind),
    ivrt: num(values.IVRT),
    eaRatio: num(values.E_A_ratio),
    mvE: num(values.MV_E),
  });

  // null = nothing usable, 9 = the engine could not decide. Both mean stay quiet.
  if (grade === null || grade === 9) {
    return null;
  }

  const intent: WordingIntent =
    grade === 0 ? 'normal' : grade === 1 ? 'mild' : grade === 2 ? 'moderate' : 'severe';

  return {
    slot: 'lv-normal-fourth',
    intent,
    reason:
      grade === 0
        ? 'ASE 2025 diastolic algorithm: normal'
        : `ASE 2025 diastolic algorithm: grade ${grade}`,
    borderline: false,
    // Reported, never written — see `advisory` on WordingSuggestion.
    advisory: true,
  };
}

/**
 * Every rule that is signed off, run against one report's measurements.
 *
 * `values` is the same display-string map the chain works on, so a value the clinician
 * has just corrected drives the wording as well.
 */
/**
 * Aorta size, one rule per structure — the sentence has a dropdown for each.
 *
 * WRITES THE NORMAL SIDE ONLY. There are no published partition values for an INDEXED
 * aortic dimension — ASE 2015 does not band them, and the conventional 2.1 cm/m² figure
 * is a single dilated/not-dilated cut-off, not the top of a "mild" band. Grading a
 * dilated aorta from an index would be inventing thresholds and printing them in a signed
 * report. `rvSize` and `raSize` decline the same way for the same reason.
 *
 * Nor is a dilated aorta raised as an advisory note: those name the phrase they suggest,
 * and the only phrase these rules can produce is the normal one — advising that for a
 * dilated aorta is worse than silence.
 */
const AORTA_INDEX_CEILING = 2.1;

function aortaIndexRule(
  slot: string,
  index: number | null,
  label: string
): WordingSuggestion | null {
  if (index === null || index > AORTA_INDEX_CEILING) {
    return null;
  }
  return {
    slot,
    intent: 'normal',
    reason: `${label} ${index} cm/m²`,
    borderline: isBorderline(index, [AORTA_INDEX_CEILING]),
  };
}

export function evaluateWordingRules(
  values: Record<string, unknown>,
  sexInput?: unknown,
  options?: { atrialFibrillation?: boolean }
): WordingSuggestion[] {
  const sex = normalizeSex(sexInput);
  const af = options?.atrialFibrillation === true;
  return [
    lvSize(values, sex),
    lvSystolicFunction(values, sex),
    lvWallThickness(values, sex),
    diastolicFunction(values, af),
    laSize(values),
    fillingPressures(values),
    rvSize(values),
    raSize(values),
    aortaIndexRule(AORTA_SLOT, num(values.ARD_Index), 'aortic root index'),
    aortaIndexRule(AORTA_ASCENDING_SLOT, num(values.Asc_Aorta_Index), 'ascending aorta index'),
  ].filter((s): s is WordingSuggestion => s !== null);
}
